import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Download, ArrowDown } from 'lucide-react';

export default function Hero() {
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden py-20 sm:py-28 lg:py-36" data-testid="hero-section">
      {/* Background - logo-matching teal/green tones */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#004d40]/[0.04] via-transparent to-[#00796b]/[0.06]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#00897b]/[0.05] blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#008751]/[0.04] blur-[100px]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="max-w-xl">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#008751]/20 bg-white/80 mb-8 opacity-0 animate-fade-in-up"
              data-testid="hero-badge"
            >
              <span className="w-2 h-2 rounded-full bg-[#E31C25]" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-600">
                {t('hero.label')}
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-black leading-[0.9] mb-6 opacity-0 animate-fade-in-up stagger-1"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-title"
            >
              {t('hero.title')}
            </h1>

            <p
              className="text-base lg:text-lg leading-relaxed text-zinc-600 mb-10 max-w-md opacity-0 animate-fade-in-up stagger-2"
              data-testid="hero-subtitle"
            >
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-wrap gap-4 mb-14 opacity-0 animate-fade-in-up stagger-3">
              <a href="#get-the-app">
                <Button
                  className="rounded-full bg-[#E31C25] text-white hover:bg-[#c41820] h-12 px-8 text-base font-semibold flex items-center gap-2 transition-transform hover:-translate-y-1 hover:shadow-lg"
                  data-testid="hero-download-btn"
                >
                  <Download className="w-5 h-5" />
                  {t('hero.cta')}
                </Button>
              </a>
              <Button
                variant="outline"
                className="rounded-full h-12 px-8 text-base font-semibold border-[#008751]/30 text-[#008751] hover:bg-[#008751]/5 transition-transform hover:-translate-y-1 flex items-center gap-2"
                onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-learn-more-btn"
              >
                {t('hero.secondary')}
                <ArrowDown className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Right - App Screenshots */}
          <div className="relative flex justify-center lg:justify-end opacity-0 animate-fade-in-up stagger-3">
            <div className="relative">
              {/* Decorative glows matching logo */}
              <div className="absolute -top-12 -left-12 w-48 h-48 rounded-full bg-[#00897b]/10 blur-3xl" />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full bg-[#E31C25]/8 blur-3xl" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-[#008751]/6 blur-[80px]" />

              <img
                src="https://customer-assets.emergentagent.com/job_ascifiw3-global/artifacts/rey5v5u1_ASC3.png"
                alt="Ascifiw3 App Screenshots"
                className="relative z-10 w-[340px] sm:w-[420px] lg:w-[520px] rounded-2xl drop-shadow-2xl"
                data-testid="hero-app-mockup"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
